import { ThemeMode, ThemePreference } from "../interfaces/theme";
import {
  DARK_THEME,
  DARK_THEME_CLASS,
  LIGHT_THEME,
  PREFERS_DARK_QUERY,
  SYSTEM_THEME,
  THEME_STORAGE_KEY,
} from "../constants/theme";

/**
 * Read the persisted preference.
 *
 * @returns The stored preference, or null when nothing valid is stored.
 */
export const getStoredPreference = (): ThemePreference | null => {
  try {
    const stored: string | null = localStorage.getItem(THEME_STORAGE_KEY);
    if (
      stored === LIGHT_THEME ||
      stored === DARK_THEME ||
      stored === SYSTEM_THEME
    ) {
      return stored;
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Persist the preference so it survives reloads and new sessions.
 *
 * @param preference Preference chosen by the user.
 */
export const saveStoredPreference = (preference: ThemePreference): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch (error) {
    console.warn("Could not save the theme preference", error);
  }
};

/**
 * The preference to start from: what was stored, otherwise follow the system.
 *
 * @returns The initial preference.
 */
export const getInitialPreference = (): ThemePreference => {
  return getStoredPreference() ?? SYSTEM_THEME;
};

/**
 * Whether the operating system currently asks for a dark appearance.
 *
 * @returns True when the system prefers dark.
 */
export const prefersDarkSystem = (): boolean => {
  try {
    return window.matchMedia(PREFERS_DARK_QUERY).matches;
  } catch (error) {
    return false;
  }
};

/**
 * Turn a preference into the theme to apply.
 *
 * @param preference The user's preference.
 * @returns The resolved theme.
 */
export const resolveTheme = (preference: ThemePreference): ThemeMode => {
  if (preference === SYSTEM_THEME) {
    return prefersDarkSystem() ? DARK_THEME : LIGHT_THEME;
  }
  return preference;
};

/**
 * Stamp or remove the dark class on <html>. Must target the same element as the
 * anti-flash script in public/index.html.
 *
 * @param theme Theme to reflect in the DOM.
 */
export const applyThemeToDocument = (theme: ThemeMode): void => {
  const root: HTMLElement = document.documentElement;
  if (theme === DARK_THEME) {
    root.classList.add(DARK_THEME_CLASS);
    return;
  }
  root.classList.remove(DARK_THEME_CLASS);
};
